import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import API from '@/config/api';
import {
  Shield, ShieldCheck, ShieldOff, Key, Smartphone, Copy, 
  CheckCircle, AlertTriangle, Eye, EyeOff, Lock
} from 'lucide-react';

export default function SecuritySettings() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Setup states
  const [showSetup, setShowSetup] = useState(false);
  const [setupStep, setSetupStep] = useState(1); // 1: password, 2: QR, 3: verify
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  
  // Disable states
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetch2FAStatus();
  }, [isAuthenticated, navigate]);

  const fetch2FAStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/auth/2fa/status`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setTwoFactorEnabled(data.enabled);
      }
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
    } finally {
      setLoading(false);
    }
  };

  const startSetup = async () => {
    if (!password) {
      setError('Please enter your password');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      
      const response = await fetch(`${API}/auth/2fa/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password })
      });

      const data = await response.json();

      if (response.ok) {
        setQrCode(data.qr_code);
        setSecret(data.secret);
        setSetupStep(2);
      } else {
        setError(data.detail || 'Failed to start 2FA setup');
      }
    } catch (err) {
      setError('Failed to start 2FA setup');
    } finally {
      setActionLoading(false);
    }
  };

  const verifySetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      
      const response = await fetch(`${API}/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: verifyCode })
      });

      const data = await response.json();

      if (response.ok) {
        setBackupCodes(data.backup_codes);
        setSetupStep(3);
        setTwoFactorEnabled(true);
      } else {
        setError(data.detail || 'Invalid verification code');
      }
    } catch (err) {
      setError('Failed to verify code');
    } finally {
      setActionLoading(false);
    }
  };

  const disable2FA = async () => {
    if (!disablePassword || !disableCode) {
      setError('Please enter your password and 2FA code');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      
      const response = await fetch(`${API}/auth/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          password: disablePassword, 
          code: disableCode 
        })
      });

      const data = await response.json();

      if (response.ok) {
        setTwoFactorEnabled(false);
        setShowDisable(false);
        setDisablePassword('');
        setDisableCode('');
        setSuccess('Two-factor authentication has been disabled');
      } else {
        setError(data.detail || 'Failed to disable 2FA');
      }
    } catch (err) {
      setError('Failed to disable 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const resetSetup = () => {
    setShowSetup(false);
    setSetupStep(1);
    setPassword('');
    setQrCode('');
    setSecret('');
    setVerifyCode('');
    setBackupCodes([]);
    setError('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] py-12 px-4" data-testid="security-settings">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Security Settings</h1>
          <p className="text-gray-400">Manage your account security</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-4 bg-emerald-500/20 border border-emerald-500/30 rounded-lg flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-emerald-400" />
            <span className="text-emerald-400">{success}</span>
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        {/* 2FA Status Card */}
        <Card className={`mb-6 ${twoFactorEnabled ? 'border-emerald-500/30' : 'border-yellow-500/30'} bg-[#14120f]`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {twoFactorEnabled ? (
                  <ShieldCheck className="h-8 w-8 text-emerald-400" />
                ) : (
                  <Shield className="h-8 w-8 text-yellow-400" />
                )}
                <div>
                  <CardTitle className="text-white">Two-Factor Authentication</CardTitle>
                  <CardDescription className="text-gray-400">
                    Add an extra layer of security to your account
                  </CardDescription>
                </div>
              </div>
              <Badge className={twoFactorEnabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}>
                {twoFactorEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {!twoFactorEnabled && !showSetup && (
              <div>
                <p className="text-gray-400 mb-4">
                  Two-factor authentication adds an extra layer of security by requiring a code from your authenticator app when signing in.
                </p>
                <Button 
                  onClick={() => setShowSetup(true)}
                  className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                >
                  <Key className="h-4 w-4 mr-2" /> Enable 2FA
                </Button>
              </div>
            )}

            {/* Setup Flow */}
            {showSetup && !twoFactorEnabled && (
              <div className="space-y-4">
                {/* Step 1: Enter Password */}
                {setupStep === 1 && (
                  <div className="space-y-4">
                    <h3 className="text-white font-medium">Step 1: Verify Your Password</h3>
                    <div>
                      <Label className="text-gray-400">Current Password</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="bg-[#0a0a0f] border-[#2d2722] text-white pr-10"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={startSetup}
                        className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                        disabled={actionLoading}
                      >
                        {actionLoading ? 'Verifying...' : 'Continue'}
                      </Button>
                      <Button variant="outline" onClick={resetSetup}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 2: Scan QR Code */}
                {setupStep === 2 && (
                  <div className="space-y-4">
                    <h3 className="text-white font-medium">Step 2: Scan QR Code</h3>
                    <p className="text-gray-400 text-sm">
                      Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                    </p>
                    
                    <div className="flex justify-center p-4 bg-white rounded-lg">
                      <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
                    </div>
                    
                    <div className="p-3 bg-[#0a0a0f] rounded-lg">
                      <p className="text-xs text-gray-400 mb-1">Can't scan? Enter this code manually:</p>
                      <div className="flex items-center gap-2">
                        <code className="text-[#D4AF37] font-mono text-sm">{secret}</code>
                        <button onClick={() => copyToClipboard(secret)} className="text-gray-400 hover:text-white">
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-gray-400">Enter 6-digit code from app</Label>
                      <Input
                        type="text"
                        value={verifyCode}
                        onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="bg-[#0a0a0f] border-[#2d2722] text-white text-center text-2xl tracking-widest"
                        placeholder="000000"
                        maxLength={6}
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        onClick={verifySetup}
                        className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                        disabled={actionLoading || verifyCode.length !== 6}
                      >
                        {actionLoading ? 'Verifying...' : 'Verify & Enable'}
                      </Button>
                      <Button variant="outline" onClick={resetSetup}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Backup Codes */}
                {setupStep === 3 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-6 w-6 text-emerald-400" />
                      <h3 className="text-emerald-400 font-medium">2FA Enabled Successfully!</h3>
                    </div>
                    
                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5" />
                        <div>
                          <p className="text-yellow-400 font-medium">Save Your Backup Codes</p>
                          <p className="text-yellow-400/80 text-sm">
                            These codes can be used to access your account if you lose your authenticator device. 
                            Save them in a secure location. They will only be shown once.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 p-4 bg-[#0a0a0f] rounded-lg">
                      {backupCodes.map((code, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-[#1c1814] rounded">
                          <code className="text-white font-mono">{code}</code>
                        </div>
                      ))}
                    </div>
                    
                    <Button 
                      onClick={() => copyToClipboard(backupCodes.join('\n'))}
                      variant="outline"
                      className="w-full"
                    >
                      <Copy className="h-4 w-4 mr-2" /> Copy All Codes
                    </Button>
                    
                    <Button 
                      onClick={resetSetup}
                      className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                    >
                      Done
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Disable 2FA */}
            {twoFactorEnabled && !showDisable && (
              <div>
                <p className="text-gray-400 mb-4">
                  Your account is protected with two-factor authentication.
                </p>
                <Button 
                  onClick={() => setShowDisable(true)}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <ShieldOff className="h-4 w-4 mr-2" /> Disable 2FA
                </Button>
              </div>
            )}

            {showDisable && (
              <div className="space-y-4">
                <h3 className="text-white font-medium">Disable Two-Factor Authentication</h3>
                <p className="text-gray-400 text-sm">
                  Enter your password and a code from your authenticator app to disable 2FA.
                </p>
                
                <div>
                  <Label className="text-gray-400">Password</Label>
                  <Input
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    className="bg-[#0a0a0f] border-[#2d2722] text-white"
                    placeholder="Enter your password"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-400">2FA Code</Label>
                  <Input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="bg-[#0a0a0f] border-[#2d2722] text-white text-center"
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={disable2FA}
                    className="bg-red-500 hover:bg-red-600 text-white"
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Disabling...' : 'Disable 2FA'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowDisable(false);
                      setDisablePassword('');
                      setDisableCode('');
                      setError('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Additional Security Info */}
        <Card className="bg-[#14120f] border-[#1c1814]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Lock className="h-5 w-5 text-[#D4AF37]" />
              Security Tips
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3 text-sm">
              <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5" />
              <span className="text-gray-400">Use a unique, strong password for your account</span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5" />
              <span className="text-gray-400">Enable 2FA to protect against unauthorized access</span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5" />
              <span className="text-gray-400">Never share your password or 2FA codes with anyone</span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5" />
              <span className="text-gray-400">Store your backup codes in a secure location</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
